import React from "react";
import { motion } from "framer-motion";
import { PageHero, Overline, PrimaryButton } from "@/components/common";
import { Reveal, staggerParent, staggerChild } from "@/lib/motion";
import { IMAGES, STORY_CHAPTERS, HOW_WE_WORK, COMPANY } from "@/data/site";

export default function Story() {
  return (
    <>
      <PageHero
        overline="Our Story"
        title="Earning our reputation since 1986"
        intro="Continuously since 1986, we've earned our reputation the honest way — through the quality of our work, our reliability, and consistent on-time, on-budget performance."
        image={IMAGES.aylesbury2}
      />

      {/* Numbered chapters */}
      <section className="container-x py-24 md:py-32">
        <div className="space-y-px">
          {STORY_CHAPTERS.map((c) => (
            <Reveal key={c.n}>
              <div className="group grid gap-6 border-t border-slate-200 py-12 md:grid-cols-12 md:gap-10 md:py-16">
                <div className="md:col-span-4">
                  <span className="font-display text-7xl font-bold leading-none text-slate-200 transition-colors group-hover:text-[#1D4ED8] md:text-8xl">
                    {c.n}
                  </span>
                </div>
                <div className="md:col-span-8">
                  <h2 className="font-display text-2xl font-medium uppercase tracking-tight text-[#0A1128] md:text-3xl">
                    {c.title}
                  </h2>
                  <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">{c.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Image band */}
      <section className="relative h-[50vh] overflow-hidden md:h-[70vh]">
        <img src={IMAGES.windsorAfter1} alt="Completed over-cladding refurbishment by Andrews Building Company" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0A1128]/40" />
        <div className="container-x absolute inset-0 flex items-end pb-12">
          <p className="max-w-xl font-display text-2xl uppercase leading-tight tracking-tight text-white md:text-4xl">
            Good old-fashioned values. Modern standards.
          </p>
        </div>
      </section>

      {/* How we work */}
      <section className="bg-[#F5F7FA] py-24 md:py-32">
        <div className="container-x">
          <Overline>How we work</Overline>
          <h2 className="mt-5 max-w-3xl font-display text-4xl font-bold uppercase tracking-tight text-[#0A1128] md:text-5xl">
            The way we've always done it
          </h2>
          <motion.div
            variants={staggerParent}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-8%" }}
            className="mt-14 grid gap-px bg-slate-200 md:grid-cols-3"
          >
            {HOW_WE_WORK.map((w) => (
              <motion.div key={w.n} variants={staggerChild} className="bg-white p-8 md:p-10">
                <span className="font-display text-sm text-[#1D4ED8]">{w.n}</span>
                <h3 className="mt-3 font-display text-xl font-medium uppercase tracking-tight text-[#0A1128]">
                  {w.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{w.text}</p>
              </motion.div>
            ))}
          </motion.div>

          <Reveal delay={0.1}>
            <div className="mt-16">
              <PrimaryButton to="/contact" testId="story-contact-btn">
                Start a Conversation
              </PrimaryButton>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
